import { BucketSize, FilterOperation, FleetWidgetType } from 'models/enums/fleet-management';
import { FleetDataFilter } from './FleetDataFilter';

export interface WidgetField {
  name: string;
  operation: FilterOperation | null;
}

interface FleetWidget {
  id: string;
  type: FleetWidgetType;
  dataset: string;
  name: string;
  description: string | null;
  fields: WidgetField[] | null;
  groups: string[] | null;
  grid: number[][] | null;
  filters: FleetDataFilter[];
  limit: number | null;
  orderBy: string[] | null;
  bucketField: string | null;
  bucket: BucketSize | null;
}

export default FleetWidget;

export interface RobotHealthTable {
  index: number;
  name: string;
  serialNumber: string;
  customer: string;
  dealer: string;
  healthStatus: string;
  generation: string;
  rswVersion: string;
  lastMission: string;
}

export const widgetDiffersToMainList = (widget: FleetWidget, list: FleetWidget[]) => {
  const isEqualArray = <T>(a: T[] | null, b: T[] | null, compareFn?: (x: T, y: T) => boolean) => {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;

    const used = new Array(b.length).fill(false);

    return a.every((itemA) => {
      const index = b.findIndex((itemB, i) => {
        if (used[i]) return false;
        return compareFn ? compareFn(itemA, itemB) : itemA === itemB;
      });

      if (index === -1) return false;
      used[index] = true;
      return true;
    });
  };

  const isEqualFilter = (a: FleetDataFilter, b: FleetDataFilter) => {
    return (
      a.id === b.id && a.field === b.field && a.type === b.type && isEqualArray(a.values, b.values)
    );
  };

  const isEqualGrid = (a: number[][] | null, b: number[][] | null) => {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      const rowA = a[i];
      const rowB = b[i];

      if (rowA.length !== rowB.length) return false;

      for (let j = 0; j < rowA.length; j++) {
        if (rowA[j] !== rowB[j]) return false;
      }
    }

    return true;
  };

  const isWidgetSame = (a: FleetWidget, b: FleetWidget) => {
    return (
      a.id === b.id &&
      a.type === b.type &&
      a.dataset === b.dataset &&
      a.name === b.name &&
      a.description === b.description &&
      isEqualArray(a.groups, b.groups) &&
      a.limit === b.limit &&
      isEqualArray(a.fields, b.fields) &&
      isEqualArray(a.orderBy, b.orderBy) &&
      a.bucketField === b.bucketField &&
      a.bucket === b.bucket &&
      isEqualGrid(a.grid, b.grid) &&
      isEqualArray(a.filters, b.filters, isEqualFilter)
    );
  };

  const existingWidget = list.find((w) => w.id === widget.id);
  if (existingWidget == null) return true;

  return !isWidgetSame(widget, existingWidget);
};

export enum HEALTH_STATUS {
  ALL = 'ALL',
  GREEN = 'GREEN',
  ORANGE = 'ORANGE',
  RED = 'RED',
  GREY = 'GREY',
}

export const STATUS_COLORS = {
  GREEN: 'rgb(52, 158, 99)',
  ORANGE: 'rgb(233, 178, 50)',
  RED: 'rgb(230, 57, 53)',
  GREY: 'rgb(51, 51, 51)',
};

export const PALETTE_COLORS = {
  BLUE_1: 'rgb(88, 63, 158)',
  BLUE_2: 'rgb(75, 105, 190)',
  BLUE_3: 'rgb(66, 130, 215)',
  BLUE_4: 'rgb(64, 150, 235)',
  BLUE_5: 'rgb(66, 165, 245)',
};
